import { useEffect, useState } from 'react';

/**
 * Responsive helper hook that reports whether the current viewport width
 * is at or below the provided breakpoint. Defaults to 768px (tablets/mobile).
 */
export function useIsMobile(breakpoint: number = 768): boolean {
    const getIsMobile = () => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return false;
        }
        return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
    };

    const [isMobile, setIsMobile] = useState<boolean>(() => getIsMobile());

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }

        const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
        const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
            setIsMobile(event.matches);
        };

        // Initial sync in case state was created before listeners attached
        setIsMobile(mediaQuery.matches);

        if (typeof mediaQuery.addEventListener === 'function') {
            mediaQuery.addEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
            return () => mediaQuery.removeEventListener('change', handleChange as (event: MediaQueryListEvent) => void);
        }

        // Safari / legacy browsers fallback
        mediaQuery.addListener(handleChange as (event: MediaQueryListEvent) => void);
        return () => mediaQuery.removeListener(handleChange as (event: MediaQueryListEvent) => void);
    }, [breakpoint]);

    return isMobile;
}
